import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Feather } from "lucide-react";
import { http } from "@/lib/api";
import Seo from "@/components/Seo";

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    } catch {
        return "";
    }
}

export default function Blog() {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        http.get("/articles")
            .then(({ data }) => setArticles(data || []))
            .catch(() => setArticles([]))
            .finally(() => setLoading(false));
    }, []);

    const blogSchema = {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "Journal — Foundation of Light",
        description: "Réflexions, enseignements et transmissions sur le langage de lumière, la guérison énergétique et l'éveil de conscience.",
        url: "https://foundation-of-light.com/journal",
        publisher: {
            "@type": "Organization",
            name: "Foundation of Light",
            logo: {
                "@type": "ImageObject",
                url: "https://foundation-of-light.com/brand/logo.png",
            },
        },
        blogPost: articles.map((a) => ({
            "@type": "BlogPosting",
            headline: a.title,
            description: a.excerpt,
            image: a.image_url,
            datePublished: a.created_at,
            url: `https://foundation-of-light.com/journal/${a.slug}`,
        })),
    };

    return (
        <div className="min-h-screen" data-testid="blog-page">
            <Seo
                title="Journal · Langage de lumière · Foundation of Light"
                description="Réflexions et transmissions autour du langage de lumière, des soins énergétiques et de l'éveil de conscience. Écrits par Foundation of Light."
                path="/journal"
                jsonLd={blogSchema}
            />
            <section className="max-w-6xl mx-auto px-6 pt-24 pb-16">
                <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium flex items-center gap-2">
                    <Feather className="w-4 h-4" /> Journal
                </div>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mb-6">Réflexions & transmissions</h1>
                <p className="text-lg text-foreground/70 max-w-2xl leading-relaxed">
                    Écrits sur le langage de lumière, les pratiques énergétiques et l'éveil de conscience. Un espace de partage libre, ouvert à toutes celles et tous ceux qui cheminent.
                </p>
            </section>

            <section className="max-w-6xl mx-auto px-6 pb-32">
                {loading ? (
                    <p className="text-muted-foreground text-center py-16">Chargement…</p>
                ) : articles.length === 0 ? (
                    <p className="text-muted-foreground text-center py-16" data-testid="blog-empty">Aucun article pour le moment.</p>
                ) : (
                    <div className="grid gap-8 md:grid-cols-2">
                        {articles.map((a, i) => (
                            <motion.article
                                key={a.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                                className="rounded-3xl border border-border/60 bg-card overflow-hidden group hover:shadow-lg transition-shadow"
                                data-testid={`blog-card-${a.slug}`}
                            >
                                <Link to={`/journal/${a.slug}`} className="block">
                                    {a.image_url && (
                                        <div className="aspect-[16/10] overflow-hidden">
                                            <img
                                                src={a.image_url}
                                                alt={a.image_alt || a.title}
                                                title={a.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                                loading="lazy"
                                            />
                                        </div>
                                    )}
                                    <div className="p-6 md:p-8">
                                        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{formatDate(a.created_at)}</p>
                                        <h2 className="font-serif text-2xl md:text-3xl mb-3 group-hover:text-primary transition-colors">{a.title}</h2>
                                        <p className="text-sm text-foreground/70 leading-relaxed mb-4">{a.excerpt}</p>
                                        <span className="inline-flex items-center gap-2 text-sm text-primary font-medium">
                                            Lire l'article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                        </span>
                                    </div>
                                </Link>
                            </motion.article>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
